import React, {useCallback, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {createSelector} from '@reduxjs/toolkit';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetProductCsvFile,
  resetRuleSet,
  setProductCsvFile
} from '@/store/slices/salesTargetSlice';

// Organisms
import {
  CategoryWrapper,
  PageContainer,
  ProductsTable
} from '@/components/organisms';
import {FileUploadWithoutModal} from '@/components/organisms/FileUploadWithoutModal';

// Molecules
import {InfoOrError, SectionTitle} from '@/components/molecules';

import ToasterAlert from '@/components/atoms/Toast/toasterAlert';
// Atoms
import {RadioButton, Tag, Toggle} from '@/components/atoms';

// Constants
import {Constants, NUMBERS} from '@/utils/constants/commonConstants';

// Utils
import {uploadFileDescriptionForCSV} from '@/utils/commonTemplate';

// Helpers
import {useProductList} from '@/containers/ProductsContainer/SalesTargetProducts/useProductList';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {
  downloadTemplateFile,
  TEMPLATE_TYPE
} from '@/services/downloadTemplateFile';
import {FONT_SIZE} from '@/utils/constants/globalStyle';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';

// Define the base cell data mapper object without the "includeExclude" key as it references functions defined later.
const cellDataMapperObj = {
  productName: ({row: {original} = {}}) => {
    return <div>{original?.productName}</div>;
  },
  productId: ({row: {original} = {}}) => {
    return <div>{original?.productId}</div>;
  },
  size: ({row: {original} = {}}) => {
    return <div>{original?.size}</div>;
  },
  status: ({row: {original} = {}}) => {
    return <div>{original?.status}</div>;
  },
  category: ({row: {original} = {}}) => {
    return <div>{original?.category}</div>;
  },
  brand: ({row: {original} = {}}) => {
    return <div>{original?.brand}</div>;
  }
};

const SalesTargetProducts = (props) => {
  const {
    setSelectedType,
    selectedType,
    uploadComplete,
    onUpdate,
    uploadError,
    setUploadError,
    selectedCategory,
    setSelectedCategory,
    ProductListColConfig,
    setRowSelection,
    rowSelection,
    handleBulkAction,
    categoryListData,
    targetType,
    isAllCategories,
    localProductData,
    setLocalProductData,
    ignoreSkuList,
    setIgnoreSku,
    name,
    showToast,
    setShowToast,
    toastMessage,
    handleIgnoreSkuChange,
    selectedCategoryInitialState,
    isEdit
  } = props;

  const dispatch = useAppDispatch();
  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    productListData,
    pageIndex,
    isLoading,
    fetchAllProducts,
    handleResetAll,
    getTagProps,
    initialSortConfig,
    searchArr
  } = useProductList();

  const selectIncludedProducts = useMemo(
    () =>
      createSelector(
        (state) => state.salesTarget?.tabWiseData?.[name]?.includedProducts,
        (includedProducts) => includedProducts || []
      ),
    [name]
  );

  const selectExcludedProducts = useMemo(
    () =>
      createSelector(
        (state) => state.salesTarget?.tabWiseData?.[name]?.excludedProducts,
        (excludedProducts) => excludedProducts || []
      ),
    [name]
  );

  const selectSelectedRowIds = useMemo(
    () =>
      createSelector(
        (state) => state.salesTarget?.tabWiseData?.[name]?.selectedRows,
        (selectedRows) => selectedRows || []
      ),
    [name]
  );

  const selectMediaFile = useMemo(
    () =>
      createSelector(
        (state) => state.salesTarget?.tabWiseData?.[name]?.mediaFile,
        (mediaFile) => mediaFile
      ),
    [name]
  );

  const includedProducts = useAppSelector(selectIncludedProducts);
  const excludedProducts = useAppSelector(selectExcludedProducts);
  const selectedRowIds = useAppSelector(selectSelectedRowIds);
  const mediaFile = useAppSelector(selectMediaFile);

  // Calculate total count of modified products (included + excluded) for header display
  const totalModifiedCount = useMemo(() => {
    const includedSet = new Set(includedProducts || []);
    const excludedSet = new Set(excludedProducts || []);
    // Combine both sets to get unique count (a product can only be in one state)
    return includedSet.size + excludedSet.size;
  }, [includedProducts, excludedProducts]);

  const getEffectiveSelectedRows = useCallback(() => {
    if (!productListData?.data?.length) return [];
    // Include both included AND excluded products as "modified" rows that should be selected
    const selectedProducts = productListData.data.filter(
      (product) =>
        selectedRowIds?.includes(product.productId) ||
        includedProducts?.includes(product.productId) ||
        excludedProducts?.includes(product.productId)
    );
    return selectedProducts;
  }, [
    productListData?.data,
    selectedRowIds,
    includedProducts,
    excludedProducts
  ]);

  const getIncludedFlag = (productId, excludedProducts, includedProducts) => {
    if (excludedProducts?.includes(productId)) return true;
    if (includedProducts?.includes(productId)) return false;
    return undefined; // If not included or excluded, return undefined
  };
  useEffect(() => {
    if (productListData?.data?.length) {
      // Rebuild local data to reflect updated includes from Redux
      const dataWithIncluded = productListData.data.map((product) => ({
        ...product,
        // Only set included to true/false if the product has been explicitly included/excluded
        included: getIncludedFlag(
          product.productId,
          excludedProducts,
          includedProducts
        )
      }));

      setLocalProductData(dataWithIncluded);
    } else {
      setLocalProductData([]);
    }
  }, [
    productListData,
    includedProducts,
    excludedProducts,
    setLocalProductData,
    ignoreSkuList,
    setIgnoreSku,
    showToast
  ]);

  const includeExclude = ({row: {original} = {}}) => {
    const {text, color} = getTagProps(original?.included);
    return color ? <Tag text={text} color={color} /> : <span>{text}</span>;
  };
  // This cell mapper uses the "included" key from local data.
  const localCellDataMapperObjLocal = {
    ...cellDataMapperObj,
    includeExclude
  };

  const cellDataMappedColsLocal = getCellDataMappedCols(
    localCellDataMapperObjLocal,
    ProductListColConfig
  );

  const handleFileDownload = useCallback(async () => {
    const body = {type: TEMPLATE_TYPE.PRODUCT_TEMPLATE};
    const response = await downloadTemplateFile(body);
    if (!response) return;
    const fileName = `SalesTargetProductTemplate_${getDateWithTimeString()}`;
    downloadCSVFiles(response, fileName);
  }, []);

  const handleDelete = useCallback(() => {
    dispatch(resetProductCsvFile({activeTab: name}));
    dispatch(resetRuleSet());
    setUploadError(false);
  }, [dispatch, name, setUploadError]);

  const handleReset = useCallback(() => {
    dispatch(resetRuleSet());
    handleResetAll();
  }, [dispatch, handleResetAll]);

  const hasMediaFile = mediaFile?.length > 0 && mediaFile[0] != null;

  const handleFileUpload = useCallback(
    (file) => {
      // When a new CSV is uploaded for this tab, reset its ruleSet so only the
      // uploaded file's product data is considered going forward.
      dispatch(resetRuleSet());
      dispatch(setProductCsvFile({activeTab: name, mediaFile: [file?.name]}));
      setUploadError(false);
    },
    [dispatch, name, setUploadError]
  );

  const handleUploadError = useCallback(
    (error) => {
      if (error) {
        setUploadError(true);
      }
    },
    [setUploadError]
  );

  // useLayoutEffect(()=>[
  //   setSelectedCategory(categoryListData)
  // ], [setSelectedCategory , categoryListData])
  return (
    <>
      <PageContainer>
        <div
          className='sales-target-component text-white'
          data-testid='sales-target-products'>
          <div className='flex items-center justify-between'>
            <SectionTitle fontSize={FONT_SIZE.XXL} label='Products' />
          </div>
          {showToast && (
            <ToasterAlert
              type='warning'
              message={toastMessage}
              show={true}
              onCloseClick={() => setShowToast(!showToast)}
            />
          )}
          <div className='radio-wrap mt-4 flex  flex-row justify-between'>
            <RadioButton
              key='rd'
              isDark={true}
              items={['Category & Products', 'Upload File with Products']}
              onUpdate={(category) => {
                setSelectedType(
                  category === NUMBERS.Zero
                    ? Constants.CategoryProducts
                    : Constants.UploadedProducts
                );
              }}
              reference='radio button'
              selected={false}
              selectedItemIndex={
                selectedType === Constants.CategoryProducts ? 0 : 1
              }
              size='small'
              className='mr-8'
              id={'uploadedProducts'}
            />
            <div>
              <Toggle
                uiSchema={{
                  label: 'Ignore SKU List',
                  isDark: true,
                  size: 'medium',
                  className: 'pre-reqsite-toggle mr-3',
                  variant: 'horizontal',
                  showCheckedIcon: false
                }}
                style={{fontSize: '18px'}}
                checked={ignoreSkuList}
                value={ignoreSkuList}
                className='text-7xl'
                size='medium'
                variant='horizontal'
                updateValue={(val) => handleIgnoreSkuChange(val, 'ignoreSku')}
              />
            </div>
          </div>
          <div className='flex items-center justify-between pt-4'>
            <InfoOrError
              info={
                'Products are not preselected. Select or upload products to include.'
              }
              className='text-xs text-gray-150'></InfoOrError>
          </div>

          <hr className='products-hr' />
          {selectedType === Constants.CategoryProducts && (
            <CategoryWrapper
              selectedCategoryInitialState={selectedCategoryInitialState}
              key={selectedType}
              categories={categoryListData}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              isAllCategories={isAllCategories}
            />
          )}
          {selectedType === Constants.UploadedProducts && (
            <FileUploadWithoutModal
              activeTab={name}
              key={selectedType}
              handleDownloadTemplate={handleFileDownload}
              handleViewTable={() => {}}
              description={uploadFileDescriptionForCSV()}
              headerTitle={Constants.UploadProducts}
              acceptedExtensions={Constants.CSV}
              files={hasMediaFile ? mediaFile : []}
              onUpload={handleFileUpload}
              feedbackType={uploadError ? Constants.Error : Constants.Info}
              helperText={
                uploadError
                  ? 'Upload Error: Please use only .CSV file type'
                  : ''
              }
              onUploadError={handleUploadError}
              onUploadDelete={handleDelete}
              variant={hasMediaFile ? Constants.Complete : Constants.Edit}
              type={Constants.Single}
              className='!max-w-full'
              containerClassName='!w-full'
              isCsvValidationRequired={true}
              required={targetType !== TARGET_TYPES.MainTarget}
              showDownloadTemplate={true}
              isValidateProductIds={true}
              productIdsFilename={mediaFile?.[0]}
              onValidatedFileName={(fileName) =>
                dispatch(
                  setProductCsvFile({activeTab: name, mediaFile: [fileName]})
                )
              }
            />
          )}
        </div>
      </PageContainer>
      {selectedType === Constants.CategoryProducts &&
        (() => {
          const effectiveRows = getEffectiveSelectedRows();

          return (
            <ProductsTable
              onUpdate={onUpdate}
              isUploadTable={false}
              columns={cellDataMappedColsLocal}
              records={localProductData}
              rowSelection={effectiveRows}
              setRowSelection={setRowSelection}
              handleBulkAction={handleBulkAction}
              setPageIndex={setPageIndex}
              setSortingObj={setSortingObj}
              setSearchArry={setSearchArry}
              searchState={searchArr}
              handleResetAll={handleReset}
              productListData={productListData}
              fetchAllProducts={fetchAllProducts}
              pageIndex={pageIndex}
              isLoading={isLoading}
              initialSortConfig={initialSortConfig}
              initialSelectedCount={totalModifiedCount}
              isEdit={isEdit}
            />
          );
        })()}
      {uploadComplete && (
        <ProductsTable
          isUploadTable={true}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      )}
    </>
  );
};

SalesTargetProducts.propTypes = {
  setSelectedType: PropTypes.func.isRequired,
  selectedType: PropTypes.string.isRequired,
  uploadComplete: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  uploadError: PropTypes.bool,
  setUploadError: PropTypes.func.isRequired,
  selectedCategory: PropTypes.object,
  setSelectedCategory: PropTypes.func.isRequired,
  ProductListColConfig: PropTypes.array.isRequired,
  setRowSelection: PropTypes.func.isRequired,
  rowSelection: PropTypes.array,
  handleBulkAction: PropTypes.func.isRequired,
  categoryListData: PropTypes.array,
  targetType: PropTypes.string,
  isAllCategories: PropTypes.bool,
  localProductData: PropTypes.array,
  setLocalProductData: PropTypes.func.isRequired,
  ignoreSkuList: PropTypes.bool,
  setIgnoreSku: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  showToast: PropTypes.bool,
  setShowToast: PropTypes.func.isRequired,
  toastMessage: PropTypes.string,
  handleIgnoreSkuChange: PropTypes.func.isRequired,
  selectedCategoryInitialState: PropTypes.object
};

SalesTargetProducts.defaultProps = {
  uploadComplete: false,
  uploadError: false,
  selectedCategory: null,
  rowSelection: [],
  categoryListData: [],
  targetType: '',
  isAllCategories: false,
  localProductData: [],
  ignoreSkuList: false,
  showToast: false,
  toastMessage: '',
  selectedCategoryInitialState: null
};

export default React.memo(SalesTargetProducts);
